import { useCallback, useEffect, useState } from "react";

// Session-lifetime cache of dropdown data (airports, hotels, crew, address types...),
// shared by every screen so a lookup is fetched once instead of on each page visit.
// Keys should include whatever the data depends on, e.g. `airports:${airlineCode}`.
const cache = new Map();

const load = (key, loader) => {
  if (!cache.has(key)) {
    const pending = Promise.resolve()
      .then(loader)
      .catch((err) => {
        cache.delete(key); // don't cache failures; next caller retries
        throw err;
      });
    cache.set(key, pending);
  }
  return cache.get(key);
};

/** Drops cached lookups whose key starts with `prefix` (call after saving that entity). */
export const invalidateLookup = (prefix) => {
  Array.from(cache.keys())
    .filter((k) => k.startsWith(prefix))
    .forEach((k) => cache.delete(k));
};

/**
 * @param {string|null} key cache key; pass null to skip loading (e.g. missing dependency)
 * @param {() => Promise<Array>} loader resolves to the list of items
 */
export default function useLookup(key, loader) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(Boolean(key));
  const [version, setVersion] = useState(0);

  useEffect(() => {
    if (!key) {
      setItems([]);
      setLoading(false);
      return undefined;
    }
    let active = true;
    setLoading(true);
    load(key, loader)
      .then((data) => active && setItems(Array.isArray(data) ? data : []))
      .catch(() => active && setItems([]))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
    // loader is intentionally not a dependency: the key identifies the data
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, version]);

  const reload = useCallback(() => {
    if (key) cache.delete(key);
    setVersion((v) => v + 1);
  }, [key]);

  return { items, loading, reload };
}
